import { useFormikContext } from 'formik';
import { useCallback, useState } from 'react';

import { type RangeFilterValue } from '@frontend/api';
import {
  Text,
  InputGroup,
  NumberInput,
  NumberInputField,
  type NumberInputProps,
  HStack,
} from '@frontend/legacy-components';

import { isRangeValue } from '../utils/validation';
import { type FormStateData } from './RulesDetailModal';
import { getInputWidth } from './constants';

type NumberValueFieldProps = Omit<NumberInputProps, 'value' | 'onChange'> & {
  value: number | RangeFilterValue;
  onChange: (value: number | RangeFilterValue) => void;
  allowDecimals?: boolean;
  signalIndex?: number;
  groupIndex?: number;
  queryIndex?: number;
  fieldPath?: string;
  valueType?: string;
  isCountQuery?: boolean;
};

export const NumberValueField = ({
  isDisabled,
  value,
  onChange,
  children,
  allowDecimals = true,
  signalIndex,
  groupIndex,
  queryIndex,
  fieldPath,
  valueType,
  isCountQuery = false,
  ...rest
}: NumberValueFieldProps) => {
  const { getFieldProps } = useFormikContext<FormStateData>();
  const [minInput, setMinInput] = useState<string>(
    isRangeValue(value) ? value.min.toString() : '',
  );
  const [maxInput, setMaxInput] = useState<string>(
    isRangeValue(value) ? value.max.toString() : '',
  );
  const [singleInput, setSingleInput] = useState<string>(
    !isRangeValue(value) ? value.toString() : '',
  );

  // Get the appropriate width based on semantic type
  const inputWidth = getInputWidth(valueType, isCountQuery);

  const parseNumber = useCallback(
    (value: string): number => {
      return allowDecimals ? parseFloat(value) : parseInt(value, 10);
    },
    [allowDecimals],
  );

  const sanitizeInput = useCallback(
    (input: string): string => {
      if (allowDecimals) return input;
      return input.replace(/[.,]/g, '');
    },
    [allowDecimals],
  );

  const handleRangeChange = useCallback(
    (
      field: 'min' | 'max',
      newValue: string,
      currentValue: RangeFilterValue,
    ) => {
      const sanitizedValue = sanitizeInput(newValue);
      const numValue = parseNumber(sanitizedValue);
      if (isNaN(numValue)) return;

      const updatedValue = { ...currentValue, [field]: numValue };
      onChange(updatedValue);
    },
    [onChange, parseNumber, sanitizeInput],
  );

  // Determine the field path for Formik
  const getFieldPath = () => {
    if (fieldPath) return fieldPath;
    if (
      signalIndex !== undefined &&
      groupIndex !== undefined &&
      queryIndex !== undefined
    ) {
      return `signals.${signalIndex}.groups.${groupIndex}.queries.${queryIndex}.value`;
    }
    return '';
  };

  const fieldProps = getFieldPath() ? getFieldProps(getFieldPath()) : {};

  return isRangeValue(value) ? (
    <HStack alignItems="center" gap={0} className="number-value-field-range">
      <NumberInput
        {...rest}
        {...fieldProps}
        value={sanitizeInput(minInput)}
        onChange={(minVal) => {
          const sanitized = sanitizeInput(minVal);
          setMinInput(sanitized);
          handleRangeChange('min', sanitized, value);
        }}
        aria-label="Minimum"
        width={inputWidth}
        className="number-value-field-min"
      >
        <NumberInputField />
      </NumberInput>
      <Text
        aria-hidden="true"
        size="sm"
        color="feature.secondary"
        className="number-value-field-separator"
        mx="-1px"
      >
        —
      </Text>
      <InputGroup
        alignItems="stretch"
        width="auto"
        sx={{
          '.select:not(:first-of-type)': {
            marginLeft: '-1px',
          },
        }}
        className="number-value-field-max-group"
      >
        <NumberInput
          {...rest}
          {...fieldProps}
          value={sanitizeInput(maxInput)}
          onChange={(maxVal) => {
            const sanitized = sanitizeInput(maxVal);
            setMaxInput(sanitized);
            handleRangeChange('max', sanitized, value);
          }}
          aria-label="Maximum"
          width={inputWidth}
          className="number-value-field-max"
        >
          <NumberInputField />
        </NumberInput>
        {children}
      </InputGroup>
    </HStack>
  ) : (
    <InputGroup
      alignItems="stretch"
      width="auto"
      sx={{
        '.select:not(:first-of-type)': {
          marginLeft: '-1px',
        },
      }}
      className="number-value-field-single"
    >
      <NumberInput
        {...rest}
        {...fieldProps}
        value={sanitizeInput(singleInput)}
        onChange={(val) => {
          const sanitized = sanitizeInput(val);
          setSingleInput(sanitized);
          const numValue = parseNumber(sanitized);
          if (!isNaN(numValue)) {
            onChange(numValue);
          }
        }}
        aria-label="Value"
        width={inputWidth}
        className="number-value-field-input"
      >
        <NumberInputField />
      </NumberInput>
      {children}
    </InputGroup>
  );
};
